import { useState } from "react";
import { BillingPayment } from "../types/payment";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import PaymentDetailsModal from "./modals/PaymentDetailsModal";

export default function PaymentsTable({ payments }: { payments: BillingPayment[] }) {
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 14;
    const [openModal, setOpenModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState<BillingPayment | null>(null);

    const totalPages = Math.ceil(payments.length / pageSize);

    const paginatedPayments = payments.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    // Format date as yyyy-mm-dd
    const formatDate = (date: Date) => {
        const options: Intl.DateTimeFormatOptions = {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        };
        return date.toLocaleString("en-US", options);
    };
    return (
        <div className="w-full">
            <div className="w-full bg-card rounded-xl p-4">
                <table className="w-full text-xs table-fixed">
                    <thead className="border-b border-gray-400 text-gray-200">
                        <tr>
                            <th className="w-1/10 text-center font-light py-2">User</th>
                            <th className="w-1/10 text-center font-light py-2">Service</th>
                            <th className="w-1/10 text-center font-light py-2">Package</th>
                            <th className="w-1/10 text-center font-light py-2">Date</th>
                            <th className="w-1/10 text-center font-light py-2">ID</th>
                            <th className="w-1/10 text-center font-light py-2">Amount</th>
                            <th className="w-1/10 text-center font-light py-2">Method</th>
                            <th className="w-1/10 text-center font-light py-2">Payer ID</th>
                            <th className="w-1/10 text-center font-light py-2">Status</th>
                            <th className="w-1/10 text-center font-light py-2">Action</th>
                        </tr>
                    </thead>

                    <tbody className="text-white">
                        {paginatedPayments.map((payment, index) => (
                            <tr key={index} className={index % 2 === 0 ? "bg-card/50" : "bg-card/30"}>
                                <td className="w-1/10 text-center py-2">{payment.username}</td>
                                <td className="w-1/10 text-center py-2">{payment.service_name}</td>
                                <td className="w-1/10 text-center py-2">{payment.product}</td>
                                <td className="w-1/10 text-center py-2">{formatDate(new Date(payment.created_at))}</td>
                                <td className="w-1/10 text-center py-2">{payment.userid ?
                                    (payment.userid.length > 13 ? `${payment.userid.slice(0, 13)}...` : payment.userid) :
                                    'N/A'
                                }</td>
                                <td className="w-1/10 text-center py-2">{payment.currency} {payment.amount}</td>
                                <td className="w-1/10 text-center py-2">{payment.payment_gateway}</td>
                                <td className="w-1/10 text-center py-2">
                                    {payment.payerid ?
                                        (payment.payerid.length > 13 ? `${payment.payerid.slice(0, 13)}...` : payment.payerid) :
                                        'N/A'
                                    }
                                </td>
                                <td className="w-1/10 text-center py-2 text-green-500">{payment.status}</td>
                                <td className="w-1/10 text-center py-2">
                                    <button className="bg-none border border-white text-white rounded-lg py-1 px-2 hover:scale-105 cursor-pointer"
                                        onClick={() => {
                                            setSelectedPayment(payment);
                                            setOpenModal(true);
                                        }}
                                    >View</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            <div className="flex justify-between items-center mt-4 text-xs text-white">
                <div className="bg-card flex items-center gap-x-2 text-white text-xs py-1 px-3 rounded-md">
                    <p>Rows per page:</p>
                    <p>{pageSize}</p>
                    <ChevronDown size={16} />
                </div>
                <div className="flex items-center gap-x-2 bg-card rounded-md">
                    <button
                        onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 rounded disabled:opacity-50"
                    >
                        <ChevronLeft />
                    </button>

                    <span>
                        {currentPage} / {totalPages}
                    </span>

                    <button
                        onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 rounded disabled:opacity-50"
                    >
                        <ChevronRight />
                    </button>
                </div>
            </div>
            <PaymentDetailsModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                payment={selectedPayment}
            />
        </div>
    )
}